import { useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { getModelContext } from "@/lib/webmcp/types";
import type { WebMcpRegistration } from "@/lib/webmcp/types";

/**
 * Registers Byteline's tools with the browser's WebMCP surface
 * (document.modelContext, or navigator.modelContext on older previews) so an
 * in-browser AI agent can use them. No-op in browsers without the API.
 *
 * The tool definitions are imported lazily once the page is idle so they stay
 * out of the first-load JavaScript.
 */
export default function WebMcpProvider() {
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    const modelContext = getModelContext();
    if (!modelContext) return;

    let cancelled = false;
    const registrations: WebMcpRegistration[] = [];

    const register = async () => {
      try {
        const { allTools } = await import("@/lib/webmcp/tools");
        if (cancelled) return;
        const tools = allTools(isAuthenticated);

        if (typeof modelContext.provideContext === "function") {
          modelContext.provideContext({ tools });
        } else if (typeof modelContext.registerTool === "function") {
          for (const tool of tools) {
            const registration = modelContext.registerTool(tool);
            if (registration) registrations.push(registration);
          }
        }
      } catch (error) {
        console.warn("WebMCP registration failed", error);
      }
    };

    const run = () => void register();
    const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number })
      .requestIdleCallback;
    const cic = (window as unknown as { cancelIdleCallback?: (handle: number) => void }).cancelIdleCallback;
    const usedIdle = typeof ric === "function";
    const handle = usedIdle ? ric!(run, { timeout: 3000 }) : (setTimeout(run, 1200) as unknown as number);

    return () => {
      cancelled = true;
      if (usedIdle && typeof cic === "function") cic(handle);
      else if (!usedIdle) clearTimeout(handle);
      try {
        for (const registration of registrations) {
          if (typeof registration.unregister === "function") registration.unregister();
          else if (typeof registration.destroy === "function") registration.destroy();
        }
        if (registrations.length === 0 && typeof modelContext.provideContext === "function") {
          modelContext.provideContext({ tools: [] });
        }
      } catch {
        // Nothing to do: the page is unloading or the API changed shape.
      }
    };
  }, [isAuthenticated]);

  return null;
}
